import FollowButton from "@/components/FollowButton";
import useAppStore from "@/context/store";
import { Button } from "antd";
import classNames from "classnames";
import { isString } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { When } from "react-if";
import {
  followExplore,
  followTopic,
  likeTopic,
  unfollowExplore,
  unfollowTopic,
  unlikeTopic,
} from "../../services";
import imageUrl, { gdImgSrcPolicy } from "../../utils/imageUrl";
import HBIcon from "../HBIcon";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";

/**
 * @typedef {Object} Suggest 推荐
 * @property {string} title
 * @property {Object} file
 * @property {string} url
 *
 * @typedef {} Explore 专题
 * @property {string} name 专题名称
 * @property {number} keyword_id 唯一id
 * @property {string} urlname 地址
 * @property {{bucket: string, farm: string, file_id: number, height: number, key: string, type: string, width: number} | string }  cover 封面背景
 * @property {string} url 是否关注
 * @property {string} description 描述
 * 专题卡片
 * @param {{
 *  topic: Suggest | Explore
 *  height?: number|string
 *  onClick?: function
 * 	className: string
 * }} props
 *
 * @returns {JSX.Element}
 */
export default function TopicCard({
  topic = {},
  height = 176,
  onClick,
  style,
  className,
  type = "default",
  isExplore = true,
  showLike = false,
}) {
  const { title, file, name, cover, urlname, url } = topic;
  const router = useRouter();
  const user = useAppStore((s) => s.user);

  const [liked, setLiked] = useState(topic.liked);
  const [following, setFollowing] = useState(topic.following);

  const imageSrc = useMemo(() => {
    if (Array.isArray(cover)) {
      return gdImgSrcPolicy(cover[0].url);
    }
    if (isString(cover)) return gdImgSrcPolicy(cover);

    return {
      src: imageUrl(file || cover, { width: 240 }),
      srcSet: imageUrl(file || cover, { width: 480 }) + " 2x",
    };
  }, [cover, file]);

  const { login } = useLogin();

  const onToggleFollow = useCallback(async () => {
    if (!user) {
      const result = await login({
        source_location: "主题关注",
      });
      if (!result) {
        return;
      }
    }

    if (following) {
      const result = isExplore
        ? await unfollowExplore(urlname)
        : await unfollowTopic({ urlname });
      if (!result.err) {
        setFollowing(false);
      }
    } else {
      const result = isExplore
        ? await followExplore(urlname)
        : await followTopic({ urlname });
      if (!result.err) {
        setFollowing(true);
      }
    }
  }, [following, isExplore, login, urlname, user]);

  const onToggleLike = useCallback(async () => {
    if (!user) {
      const result = await login({
        source_location: "主题喜欢",
      });
      if (!result) {
        return;
      }
    }

    if (liked) {
      const result = await unlikeTopic({ urlname });
      if (!result.err) {
        setLiked(false);
      }
    } else {
      const result = await likeTopic({ urlname });
      if (!result.err) {
        setLiked(true);
      }
    }
  }, [liked, login, urlname, user]);

  const onLink = (e) => {
    e.preventDefault();
    if (url || urlname) {
      if (type === "default") {
        // 如果配置了全链，新开窗口跳转
        if (url && url.startsWith("http")) {
          window.open(url);
          return;
        }
        router.push(url || `/explore/${urlname}`);
      } else {
        window.open(url || `/explore/${urlname}`);
      }
    }
    onClick && onClick();
  };

  return (
    <div className={classNames([styles.card, className])} style={style}>
      <a
        className={styles.topic}
        style={{ height: height + "px" }}
        href={url || `/explore/${urlname}`}
        onClick={onLink}
        title={topic.description}
        data-gd-expose="topic_expose"
        data-gd-click="topic_click"
        data-topic-id={topic.keyword_id}
        data-topic-title={topic.name}
      >
        <img className={styles.img} {...imageSrc} alt={title || name} />
        <span className={styles.name}>{title || name}</span>
      </a>
      <When condition={showLike}>
        <Button
          className={classNames([styles.favour, liked ? styles.active : null])}
          data-gd-click="button_click"
          data-button-name={liked ? "取消喜欢" : "喜欢"}
          onClick={onToggleLike}
        >
          <HBIcon type={liked ? "ic_favorite" : "favorite"} />
        </Button>
      </When>
      <When condition={type === "primary"}>
        <div style={{ marginTop: "8px" }}>
          <FollowButton
            followed={following}
            onClick={onToggleFollow}
            data-gd-click="button_click"
            data-button-name={following ? "取消关注" : "关注"}
            data-concern-object-type="专题"
            data-concern-object-name={title || name}
            grayFollow
          />
        </div>
      </When>
    </div>
  );
}
